import { useState, useEffect, useRef } from "react";

// Exchanges with full support
const FULL_SUPPORT    = new Set(["NYSE","NASDAQ","AMEX","OTC","JK","IDX"]);
// Exchanges coming soon
const COMING_SOON = new Set(["NS","BO","NSE","BSE","L","PA","DE","MI","MC","AS","TO","AX","HK","KL","SI","AE","DU","AD","SR","QA","KW","T","KS","TW","JO","SA","NZ","V","CN","MX","BA","TA","IR"]);

function getExchangeTag(exchange, symbol) {
  const suffix = symbol?.includes(".") ? symbol.split(".").pop() : "";
  const ex = exchange || suffix;

  if (FULL_SUPPORT.has(ex) || FULL_SUPPORT.has(suffix)) {
    return { label: ex || "US", color: "#00C896", bg: "rgba(0,200,150,0.1)", border: "rgba(0,200,150,0.2)" };
  }
  if (COMING_SOON.has(ex) || COMING_SOON.has(suffix)) {
    return { label: "Coming Soon", color: "#FFB020", bg: "rgba(255,176,32,0.08)", border: "rgba(255,176,32,0.2)" };
  }
  return { label: ex || "—", color: "#6B7280", bg: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.2)" };
}

const S = {
  wrap:     { position:"relative", width:"100%" },
  input:    { width:"100%", padding:"12px 16px 12px 42px", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:12, color:"#E8EAF0", fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:"inherit", transition:"border-color 0.2s" },
  icon:     { position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:"#4A5568", fontSize:18 },
  dropdown: { position:"absolute", top:"calc(100% + 6px)", left:0, right:0, background:"#111827", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, zIndex:200, overflow:"hidden", boxShadow:"0 20px 40px rgba(0,0,0,0.5)" },
  item:     { padding:"12px 16px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid rgba(255,255,255,0.04)", transition:"background 0.15s" },
};

export default function SearchBar({ onSearch, searchResults, searching, onSelect }) {
  const [query, setQuery]       = useState("");
  const [focused, setFocused]   = useState(false);
  const [selected, setSelected] = useState(false); // prevent re-search after selection
  const timer = useRef(null);
  const ref   = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (selected) return; // don't search after selection
    if (query.length >= 2) {
      timer.current = setTimeout(() => onSearch(query), 300);
    }
    return () => clearTimeout(timer.current);
  }, [query, onSearch, selected]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setFocused(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSelect = (item) => {
    setSelected(true);
    setQuery(item.symbol); // show ticker not full name — cleaner
    setFocused(false);
    onSelect(item.symbol);
  };

  const handleChange = (e) => {
    setSelected(false); // allow search again when user types
    setQuery(e.target.value);
  };

  const showDropdown = focused && query.length >= 2 && (searchResults.length > 0 || searching);

  return (
    <div style={S.wrap} ref={ref}>
      <span style={S.icon}>⌕</span>
      <input
        style={{ ...S.input, borderColor:focused?"rgba(0,200,150,0.4)":"rgba(255,255,255,0.1)" }}
        placeholder="Search by company name or ticker — 'Apple', 'Tesla', 'Telkom'..."
        value={query}
        onChange={(e) => handleChange(e)}
        onFocus={() => setFocused(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && searchResults.length > 0) handleSelect(searchResults[0]);
        }}
      />
      {showDropdown && (
        <div style={S.dropdown}>
          {searching && (
            <div style={{ padding:"12px 16px", color:"#4A5568", fontSize:13 }}>
              Searching for "{query}"...
            </div>
          )}
          {!searching && searchResults.length === 0 && (
            <div style={{ padding:"12px 16px", color:"#4A5568", fontSize:13 }}>
              No results found for "{query}"
            </div>
          )}
          {searchResults.map((item) => {
            const tag = getExchangeTag(item.exchangeShortName, item.symbol);
            const isComingSoon = tag.label === "Coming Soon";
            return (
              <div
                key={item.symbol}
                style={{ ...S.item, opacity: isComingSoon ? 0.7 : 1 }}
                onMouseDown={() => handleSelect(item)}
                onMouseEnter={(e) => e.currentTarget.style.background="rgba(255,255,255,0.04)"}
                onMouseLeave={(e) => e.currentTarget.style.background="transparent"}
              >
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div>
                    <div style={{ color:"#9CA3AF", fontSize:13, marginBottom:2 }}>{item.name}</div>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontWeight:700, color:"#00C896", fontFamily:"'Syne',sans-serif", fontSize:12 }}>
                        {item.symbol}
                      </span>
                      {isComingSoon && (
                        <span style={{ fontSize:9, color:"#FFB020" }}>🔜 Full screening coming soon</span>
                      )}
                    </div>
                  </div>
                </div>
                <span style={{ fontSize:10, fontWeight:600, color:tag.color, background:tag.bg, border:`1px solid ${tag.border}`, padding:"2px 8px", borderRadius:6, whiteSpace:"nowrap" }}>
                  {tag.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
